﻿/**
 * Canvas 印章渲染工具 — 与后端 stamp_service.py 镜像
 * 用途：印章 Modal 右侧实时预览
 */

const RED = '#cc0000';
const RED_OLD = '#be3c32';

// 获取可用中文字体
function pickFont(weight = 'normal') {
  const candidates = weight === 'bold' ? [
    'bold 24px "SimHei", "Microsoft YaHei", "PingFang SC", "Heiti SC", sans-serif',
    'bold 24px "Microsoft YaHei Bold", "Microsoft YaHei", sans-serif',
  ] : [
    '24px "SimHei", "Microsoft YaHei", "PingFang SC", "Heiti SC", sans-serif',
    '24px "Microsoft YaHei", "PingFang SC", sans-serif',
    '24px sans-serif',
  ];
  return candidates[0];
}

function getCtx(canvas, overrideW, overrideH) {
  const dpr = window.devicePixelRatio || 1;
  let w, h;
  if (overrideW !== undefined && overrideH !== undefined) {
    // 强制使用传入的尺寸（用于预览区按印章实际比例渲染）
    w = overrideW;
    h = overrideH;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
  } else {
    const rect = canvas.getBoundingClientRect();
    // 防御：canvas 尺寸极小时强制最小尺寸，避免负半径
    w = Math.max(rect.width, 20);
    h = Math.max(rect.height, 20);
    canvas.width = w * dpr;
    canvas.height = h * dpr;
  }
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);
  ctx.clearRect(0, 0, w, h);
  return { ctx, w, h };
}

/**
 * 沿圆形弧写一个字符，文字径向排列（每个字垂直于圆周）
 * deg: 角度（度），0=右侧，90=顶部，180=左侧，270=底部
 * 文字 baseline 垂直于半径方向
 */
function drawRadialArcChar(ctx, ch, cx, cy, radius, deg, fontSize, color, flip = false) {
  const phi = deg * Math.PI / 180;
  const x = cx + radius * Math.cos(phi);
  const y = cy - radius * Math.sin(phi);  // 正确：90°→顶部
  ctx.save();
  ctx.translate(x, y);
  // 旋转 φ-90° 使 baseline 水平（文字正立），top 朝圆弧外侧
  // 顶(φ=90°): 0°；右侧(φ=0°): -90°；底部(φ=270°): 180°
  // flip=true: 旋转+180° 使文字倒立（用于下弦文）
  const flipRad = flip ? Math.PI : 0;
  ctx.rotate((90 - deg) * Math.PI / 180 + flipRad);
  ctx.font = `${fontSize}px "SimHei", "Microsoft YaHei", sans-serif`;
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(ch, 0, 0);
  ctx.restore();
}

/**
 * 沿椭圆弧写一个字符，文字径向排列（每个字垂直于椭圆周）
 */
function drawRadialEllipseArcChar(ctx, ch, cx, cy, rx, ry, deg, fontSize, color) {
  const phi = deg * Math.PI / 180;
  const x = cx + rx * Math.cos(phi);
  const y = cy - ry * Math.sin(phi);
  ctx.save();
  ctx.translate(x, y);
  // 椭圆切线角：使文字水平正立（取消径向旋转）
  const theta = -(deg - 90) * Math.PI / 180;
  ctx.rotate(theta);
  ctx.font = `${fontSize}px "SimHei", "Microsoft YaHei", sans-serif`;
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(ch, 0, 0);
  ctx.restore();
}

/**
 * 在椭圆弧上按「等弧长」分布 n 个字符的角度（中心在 centerDeg，左右对称，阅读从左到右）
 * 解决等角度分布导致的「顶点字间距大、两侧挤在一起」问题
 */
function ellipseEqualArcAngles(rx, ry, centerDeg, spacingPx, n) {
  const center = centerDeg * Math.PI / 180;
  const dsdphi = (phi) => Math.sqrt((rx * Math.sin(phi)) ** 2 + (ry * Math.cos(phi)) ** 2);
  // 从 center 向「阅读者左侧(φ→180°)」与「阅读者右侧(φ→0°)」两个方向累积弧长
  const buildToward = (targetDeg) => {
    const pts = [{ len: 0, phi: center }];
    let phi = center, len = 0;
    let d = targetDeg - centerDeg;
    while (d > 180) d -= 360;
    while (d < -180) d += 360;
    const dir = d >= 0 ? 1 : -1;
    const stepR = (0.05 * Math.PI / 180) * dir;
    for (let k = 0; k < 40000; k++) {
      const ds = dsdphi(phi) * Math.abs(stepR);
      len += ds; phi += stepR;
      pts.push({ len, phi });
      if (len > spacingPx * (n + 2)) break;
    }
    return pts;
  };
  const towardLeft = buildToward(180);  // 阅读者左侧（屏幕 x 较小）
  const towardRight = buildToward(0);    // 阅读者右侧（屏幕 x 较大）
  const sampleAt = (pts, targetLen) => {
    for (let k = 1; k < pts.length; k++) {
      if (pts[k].len >= targetLen) {
        const a = pts[k - 1], b = pts[k];
        const t = (targetLen - a.len) / ((b.len - a.len) || 1);
        return (a.phi + (b.phi - a.phi) * t) * 180 / Math.PI;
      }
    }
    return pts[pts.length - 1].phi * 180 / Math.PI;
  };
  const half = (n - 1) / 2;
  const angles = [];
  for (let i = 0; i < n; i++) {
    const offset = (i - half) * spacingPx;  // <0 左, >0 右
    angles.push(offset <= 0 ? sampleAt(towardLeft, -offset) : sampleAt(towardRight, offset));
  }
  return angles;
}

// ═══════════════════════════ 入库章（矩形，固定 200×80） ═══════════════════════════
function drawWarehouseStamp(ctx, w, h, cfg) {
  const { stampText = '', bottomText = '', color = RED } = cfg;
  // 固定宽高比 200:80 = 2.5:1，canvas 按比例填充
  const stampW = 200;
  const stampH = 80;
  const scale = Math.min(w / stampW, h / stampH);
  const sw = stampW * scale;
  const sh = stampH * scale;
  const x0 = (w - sw) / 2;
  const y0 = (h - sh) / 2;

  // 单边框
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.strokeRect(x0, y0, sw, sh);

  // 公司名（上半）
  const maxNameW = sw - 16;
  let nameSize = Math.min(28 * scale, maxNameW / (stampText.length || 1) * 1.2, maxNameW * 0.7);
  nameSize = Math.max(nameSize, 6);
  ctx.font = `bold ${nameSize}px "SimHei", "Microsoft YaHei", sans-serif`;
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(stampText, w / 2, y0 + sh * 0.32);

  // 中间分隔线
  ctx.beginPath();
  ctx.moveTo(x0 + 10, y0 + sh * 0.52);
  ctx.lineTo(x0 + sw - 10, y0 + sh * 0.52);
  ctx.stroke();

  // 底部文字（下半）
  let botSize = Math.min(18 * scale, maxNameW / (bottomText.length || 1) * 1.2, maxNameW * 0.6);
  botSize = Math.max(botSize, 6);
  ctx.font = `${botSize}px "SimHei", "Microsoft YaHei", sans-serif`;
  ctx.fillText(bottomText, w / 2, y0 + sh * 0.76);
}

// ═══════════════════════════ 圆形公章（单边框） ═══════════════════════════
function drawRoundCompany(ctx, w, h, cfg, options = {}) {
  const {
    stampText = '',
    topText = stampText,
    bottomText = '',               // 空字符串：与后端一致，不设置时不绘制
    centerText = stampText,
    showStar = true,
    color = RED,
    bold = false,
    topFontSize: extTopFs = 0,      // 外部传入字号（0=自动）
    centerFontSize: extCenterFs = 0,
    botFontSize: extBotFs = 0,
  } = cfg;
  const { useEllipse = false, rx: optRx, ry: optRy } = options;
  const cx = w / 2;
  const cy = h / 2;
  const r = Math.min(w, h) / 2 - 8;  // 圆形/椭圆共用：取宽高最小值的一半
  const rxVal = optRx !== undefined ? optRx : r;   // 椭圆水平半径（默认=圆形半径）
  const ryVal = optRy !== undefined ? optRy : r;   // 椭圆垂直半径（默认=圆形半径）

  // 单圈（椭圆时 rx/ry 决定实际形状）
  ctx.strokeStyle = color;
  ctx.lineWidth = 5;  // 边线宽度
  ctx.beginPath();
  if (useEllipse) {
    ctx.ellipse(cx, cy, Math.max(rxVal, 1), Math.max(ryVal, 1), 0, 0, 2 * Math.PI);
  } else {
    ctx.arc(cx, cy, Math.max(r, 1), 0, 2 * Math.PI);
  }
  ctx.stroke();

  const fontWeight = bold ? 'bold ' : '';
  const minDim = Math.min(w, h);
  // 字号：外部传入则用外部，否则自动计算
  const topFontSize = extTopFs > 0 ? extTopFs : Math.max(minDim * 0.09, 8);
  const centerFontSize = extCenterFs > 0 ? extCenterFs : Math.max(minDim * 0.13, 8);
  const starFontSize = Math.max(minDim * 0.16, 8);
  const botFontSize = extBotFs > 0 ? extBotFs : Math.max(minDim * 0.07, 6);

  // 顶部弧形文字（沿上边线排列，径向排列，方向朝外，字数不限）
  if (topText) {
    const text = topText;
    const n = text.length;
    if (n > 0) {
      // 自适应字间距：字号越大，字间距越大
      const baseDeg = n >= 8 ? 10 : 12;
      const degEach = Math.round(Math.min(Math.max(baseDeg * (topFontSize / 18), 7), 18));
      if (useEllipse) {
        // 椭圆：等弧长分布，解决两侧拥挤问题；字间距 = 字号 × 1.25
        const spacingPx = topFontSize * 1.25;
        const angles = ellipseEqualArcAngles(rxVal - 15, ryVal - 15, 90, spacingPx, n);
        for (let i = 0; i < n; i++) {
          drawRadialEllipseArcChar(ctx, text[i], cx, cy, rxVal - 15, ryVal - 15, angles[i], topFontSize, color);
        }
      } else {
        // 圆形：等弧长分布，字间距 = 字号 × 1.25，与椭圆统一
        const spacingPx = topFontSize * 1.25;
        const total = n * spacingPx;  // 总弧长（px），不设上限
        const arcR = Math.max(r - 15, 1);
        for (let i = 0; i < n; i++) {
          const t = (i + 0.5) / n;  // 0~1 均匀分布
          const arcLen = t * total;  // 累计弧长
          const deg = 90 - (arcLen - total / 2) / arcR * (180 / Math.PI);  // 逆时针  // 转角度
          drawRadialArcChar(ctx, text[i], cx, cy, arcR, deg, topFontSize, color);
        }
      }
    }
  }

  // 中心文字
  if (centerText) {
    ctx.font = `${fontWeight}${centerFontSize}px "SimHei", "Microsoft YaHei", "PingFang SC", "Heiti SC", sans-serif`;
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    if (showStar) {
      // 五角星 + 中心文字（文字在星下方）
      ctx.font = `${fontWeight}${starFontSize}px "SimHei", "Microsoft YaHei", sans-serif`;
      ctx.fillText('★', cx, cy - 15);
      ctx.font = `${fontWeight}${centerFontSize}px "SimHei", "Microsoft YaHei", sans-serif`;
      ctx.fillText(centerText, cx, cy + 20);  // 向下移 20px
    } else {
      ctx.fillText(centerText, cx, cy);
    }
  }

  // 底部弧形文字（沿下边线排列，径向排列，方向朝外，字数不限）
  if (bottomText) {
    const text = bottomText;
    const n = text.length;
    if (n > 0) {
      // 自适应字间距：字号越大，字间距越大
      const baseDeg = 11;
      const degEach = Math.round(Math.min(Math.max(baseDeg * (botFontSize / 16), 7), 15));
      if (useEllipse) {
        // 椭圆：等弧长分布，字间距 = 字号 × 1.5
        const spacingPx = botFontSize * 1.5;
        const angles = ellipseEqualArcAngles(rxVal - 15, ryVal - 15, 270, spacingPx, n);
        for (let i = 0; i < n; i++) {
          drawRadialEllipseArcChar(ctx, text[i], cx, cy, rxVal - 15, ryVal - 15, angles[i], botFontSize, color);
        }
      } else {
        // 圆形：等弧长分布，字间距 = 字号 × 1.25，与椭圆统一
        const spacingPx = botFontSize * 1.25;
        const total = n * spacingPx;
        const arcR = Math.max(r - 15, 1);
        for (let i = 0; i < n; i++) {
          const t = (i + 0.5) / n;
          const arcLen = t * total;
          const deg = 270 - (arcLen - total / 2) / arcR * (180 / Math.PI);  // 逆时针，与顶部一致
          drawRadialArcChar(ctx, text[i], cx, cy, arcR, deg, botFontSize, color);
        }
      }
    }
  }
}

// ═══════════════════════════ 圆形纯弧文字（无中心，单边框） ═══════════════════════════
function drawArcOnlyCompany(ctx, w, h, cfg) {
  const {
    stampText = '',
    topText = stampText,
    bottomText = '',
    showStar = true,
    color = RED,
  } = cfg;
  const cx = w / 2;
  const cy = h / 2;
  const r = Math.max(Math.min(w, h) / 2 - 8, 1);

  ctx.strokeStyle = color;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, 2 * Math.PI);
  ctx.stroke();

  const scale = Math.min(w, h) / 460;
  // 顶部弧文字（密度更高）
  if (topText) {
    const arcText = topText.slice(0, 14);
    const n = arcText.length;
    const degEach = n >= 10 ? 9 : 11;
    const total = Math.min(n * degEach, 150);
    const start = 90 + total / 2;
    const arcR = r - 38;
    for (let i = 0; i < n; i++) {
      const deg = start - (i + 0.5) * (total / n);
      drawRadialArcChar(ctx, arcText[i], cx, cy, arcR, deg, 24 * scale, color);
    }
  }

  // 中心大五角星
  if (showStar) {
    ctx.font = `bold ${72 * scale}px "SimHei", "Microsoft YaHei", "PingFang SC", sans-serif`;
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('★', cx, cy);
  }

  // 底部弧文字
  if (bottomText) {
    const bot = bottomText.slice(0, 12);
    const n = bot.length;
    const total = Math.min(n * 11, 130);
    const start = 270 - total / 2;
    const arcR = r - 36;
    for (let i = 0; i < n; i++) {
      const deg = start + (i + 0.5) * (total / n);
      drawRadialArcChar(ctx, bot[i], cx, cy, arcR, deg, 22 * scale, color, true);
    }
  }
}

// ═══════════════════════════ 方形个人章 ═══════════════════════════
function drawSquarePersonal(ctx, w, h, cfg) {
  const { stampText = '', showStar = true, color = RED } = cfg;
  const size = Math.min(w, h) * 0.7;
  const x0 = (w - size) / 2;
  const y0 = (h - size) / 2;
  const bd = 2;

  ctx.strokeStyle = color;
  ctx.lineWidth = bd;
  ctx.strokeRect(x0, y0, size, size);

  const name = stampText.slice(0, 4);
  const fs = { 1: 64, 2: 56, 3: 50, 4: 40 }[name.length] || 32;
  const scale = size / 220;
  const fontSize = Math.max(fs * scale, 6);
  ctx.font = `bold ${fontSize}px "SimHei", "Microsoft YaHei", "PingFang SC", "Heiti SC", sans-serif`;
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const nameY = y0 + size / 2 + (showStar ? 8 : 0);
  ctx.fillText(name, w / 2, nameY);

  if (showStar) {
    ctx.font = `${20 * scale}px "SimHei", "Microsoft YaHei", sans-serif`;
    ctx.fillText('★', w / 2, y0 + 18 * scale);
  }

  // 底部标签
  ctx.font = `${16 * scale}px "SimHei", "Microsoft YaHei", sans-serif`;
  ctx.fillText('签名印章', w / 2, nameY + fontSize * 0.7);
}

// ═══════════════════════════ 圆形个人章 ═══════════════════════════
function drawRoundPersonal(ctx, w, h, cfg) {
  const { stampText = '', showStar = true, color = RED } = cfg;
  const cx = w / 2;
  const cy = h / 2;
  const r = Math.max(Math.min(w, h) / 2 - 14, 1);
  const scale = Math.min(w, h) / 280;

  ctx.strokeStyle = color;
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, 2 * Math.PI);
  ctx.stroke();
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(cx, cy, Math.max(r - 12, 1), 0, 2 * Math.PI);
  ctx.stroke();

  const name = stampText.slice(0, 4);
  const fs = { 1: 64, 2: 56, 3: 46, 4: 40 }[name.length] || 36;
  const fontSize = Math.max(fs * scale, 6);

  let curY = cy;
  if (showStar) {
    ctx.font = `bold ${28 * scale}px "SimHei", "Microsoft YaHei", sans-serif`;
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('★', cx, curY - 30 * scale);
    curY += 10 * scale;
  }

  ctx.font = `bold ${fontSize}px "SimHei", "Microsoft YaHei", "PingFang SC", "Heiti SC", sans-serif`;
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(name, cx, curY);

  ctx.font = `${Math.max(16 * scale, 4)}px "SimHei", "Microsoft YaHei", sans-serif`;
  ctx.fillText('签名印章', cx, curY + fontSize * 0.8);
}

// ═══════════════════════════ 椭圆个人章 ═══════════════════════════
function drawOvalPersonal(ctx, w, h, cfg) {
  const { stampText = '', showStar = true, color = RED } = cfg;
  const cx = w / 2;
  const cy = h / 2;
  const rx = Math.min(w, h * 1.5) / 2 - 12;
  const ry = Math.min(w, h * 1.5) / 3 - 12;
  const scale = Math.min(w, h) / 280;

  ctx.strokeStyle = color;
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.ellipse(cx, cy, Math.max(rx, 1), Math.max(ry, 1), 0, 0, 2 * Math.PI);
  ctx.stroke();
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.ellipse(cx, cy, Math.max(rx - 10, 1), Math.max(ry - 10, 1), 0, 0, 2 * Math.PI);
  ctx.stroke();

  const name = stampText.slice(0, 4);
  const fs = { 1: 64, 2: 56, 3: 46, 4: 40 }[name.length] || 36;
  const fontSize = Math.max(fs * scale, 6);

  let curY = cy;
  if (showStar) {
    ctx.font = `bold ${24 * scale}px "SimHei", "Microsoft YaHei", sans-serif`;
    ctx.fillStyle = color;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('★', cx, curY - 28 * scale);
    curY += 4 * scale;
  }

  ctx.font = `bold ${fontSize}px "SimHei", "Microsoft YaHei", "PingFang SC", "Heiti SC", sans-serif`;
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(name, cx, curY);

  ctx.font = `${Math.max(16 * scale, 4)}px "SimHei", "Microsoft YaHei", sans-serif`;
  ctx.fillText('签名印章', cx, curY + fontSize * 0.8);
}

// ═══════════════════════════ 做旧效果 ═══════════════════════════
function applyAged(ctx, w, h, intensity = 0.5) {
  // 取整 canvas 为 image data
  const imageData = ctx.getImageData(0, 0, w * (window.devicePixelRatio || 1), h * (window.devicePixelRatio || 1));
  const data = imageData.data;
  const dpr = window.devicePixelRatio || 1;
  const totalW = data.length / 4;

  // 1) 褪色 + 偏暗红
  for (let i = 0; i < totalW; i++) {
    const idx = i * 4;
    const a = data[idx + 3];
    if (a > 0) {
      data[idx] = Math.min(255, Math.round(data[idx] * 0.85 + 40 * intensity));
      data[idx + 1] = Math.min(255, Math.round(data[idx + 1] * 0.7 + 30 * intensity));
      data[idx + 2] = Math.min(255, Math.round(data[idx + 2] * 0.7 + 30 * intensity));
      data[idx + 3] = Math.round(a * (0.85 + 0.15 * (1 - intensity)));
    }
  }
  ctx.putImageData(imageData, 0, 0);

  // 2) 随机擦除斑点
  const spotCount = Math.round(intensity * 80);
  for (let i = 0; i < spotCount; i++) {
    const sx = Math.random() * w;
    const sy = Math.random() * h;
    const sr = 1 + Math.random() * 3;
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(sx, sy, sr, 0, 2 * Math.PI);
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';
  }
}

// ═══════════════════════════ 统一入口 ═══════════════════════════

/**
 * 渲染印章到 canvas
 * @param {HTMLCanvasElement} canvas
 * @param {object} cfg
 * @param {string} cfg.type - 'company' | 'personal'
 * @param {string} cfg.style - 印章样式
 * @param {string} cfg.stampText - 中心文字
 * @param {string} cfg.topText - 抬头文字
 * @param {string} cfg.bottomText - 下弦文字
 * @param {boolean} cfg.showStar - 是否显示五角星
 * @param {boolean} cfg.aged - 是否做旧
 */
export function renderSealToCanvas(canvas, cfg) {
  if (!canvas) return;
  const { ctx, w, h } = getCtx(canvas, cfg.width, cfg.height);
  // 不再填充白色背景 — 保留透明底色，盖章时不会遮挡文件内容

  const baseColor = cfg.aged ? RED_OLD : RED;
  const c = { ...cfg, color: baseColor };

  const { type = 'company', style = 'rect' } = cfg;

  if (type === 'company') {
    switch (style) {
      case 'round':
        drawRoundCompany(ctx, w, h, c);
        break;
      case 'round_thick':
        drawRoundCompany(ctx, w, h, { ...c, bold: true });
        break;
      case 'oval':
        drawRoundCompany(ctx, w, h, { ...c, stampText: c.stampText }, { useEllipse: true, rx: w / 2 - 8, ry: h / 2 - 8 });
        break;
      case 'arc_only':
        drawArcOnlyCompany(ctx, w, h, c);
        break;
      case 'arc_center':
        drawRoundCompany(ctx, w, h, { ...c, bold: true });
        break;
      default:
        drawRoundCompany(ctx, w, h, c);
    }
  } else {
    switch (style) {
      case 'square':
        drawSquarePersonal(ctx, w, h, c);
        break;
      case 'round':
        drawRoundPersonal(ctx, w, h, c);
        break;
      case 'oval':
        drawOvalPersonal(ctx, w, h, c);
        break;
      default:
        drawSquarePersonal(ctx, w, h, c);
    }
  }

  // 做旧
  if (cfg.aged) {
    applyAged(ctx, w, h, 0.5);
  }
}

// ═══════════════════════════ 缩略图（带中文示意） ═══════════════════════════

/**
 * 在指定 canvas 上绘制印章样式缩略图（用于选择器）
 * 使用默认演示文字
 */
export function renderStyleThumb(canvas, type, style) {
  if (!canvas) return;
  const { ctx, w, h } = getCtx(canvas);
  ctx.fillStyle = '#fafafa';
  ctx.fillRect(0, 0, w, h);

  const demo = type === 'company'
    ? { stampText: '电子专用章', topText: '上海某某医疗科技有限公司', bottomText: '入库专用章' }
    : { stampText: '张三', topText: '', bottomText: '签名印章' };

  renderSealToCanvas(canvas, { type, style, ...demo, showStar: true, aged: false });
}

// 兼容别名
/**
 * @param {HTMLCanvasElement} canvas
 * @param {object} cfg
 */
export function sealCanvas(canvas, cfg) {
  // 映射三个独立字号到绘制函数接受的字段名
  return renderSealToCanvas(canvas, {
    ...cfg,
    botFontSize: cfg.bottomFontSize ?? 0,
  });
}

export const COMPANY_STYLES = [
  { value: 'round', label: '圆形章' },
  { value: 'oval', label: '椭圆章' },
];

export const PERSONAL_STYLES = [
  { value: 'square', label: '方形印章' },
  { value: 'round', label: '圆形印章' },
  { value: 'oval', label: '椭圆印章' },
];

